import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight, Trophy } from "lucide-react";
import { MOMENTS } from "./data";
import { publicList } from "../admin/api";
import { resolveFileUrl } from "./fileUrl";

const TAG_COLOR = {
  Champion: "#e6c769",
  Tournament: "#fbbf24",
  Networking: "#a5b4fc",
  Moment: "#e6c769",
};

const Moments = () => {
  const [open, setOpen] = React.useState(null);
  const [moments, setMoments] = React.useState(MOMENTS);

  // Merge uploaded images (admin → Images tab) into the gallery.
  React.useEffect(() => {
    let cancelled = false;
    publicList("images")
      .then((imgs) => {
        if (cancelled || !Array.isArray(imgs) || imgs.length === 0) return;
        const uploaded = imgs
          .filter((img) => img.url)
          .map((img) => ({
            id: img.id,
            image: resolveFileUrl(img.url),
            title: img.title || "Untitled",
            caption:
              img.caption ||
              [img.location, img.date].filter(Boolean).join(" · ") ||
              "From the archive",
            tag: img.category || "Moment",
          }));
        const uploadedIds = new Set(uploaded.map((u) => u.id));
        setMoments([
          ...uploaded,
          ...MOMENTS.filter((m) => !uploadedIds.has(m.id)),
        ]);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section id="moments" className="relative py-28 px-6 md:px-10">
      <div className="max-w-[1300px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="text-[11px] tracking-[0.4em] uppercase text-[#e6c769]/80 font-mono">
            Frames · From the archive
          </div>
          <div className="mt-3 grid lg:grid-cols-2 gap-10 items-end">
            <h2
              className="text-[44px] md:text-[80px] leading-[0.95] tracking-tight text-white"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              <span className="italic">Moments</span> that mattered.
            </h2>
            <p className="text-white/55 text-[15px] leading-relaxed max-w-md justify-self-end">
              Trophies earned, rooms entered, hands shaken. Click any frame to see it full.
            </p>
          </div>
        </motion.div>

        {/* Editorial collage */}
        <div className="grid grid-cols-12 gap-4 md:gap-5">
          {moments.map((m, i) => {
            // Editorial layout — vary tile sizes for magazine feel
            const layout = [
              "col-span-12 md:col-span-7 row-span-2 aspect-[4/5] md:aspect-auto md:h-[560px]",
              "col-span-12 md:col-span-5 aspect-[4/3] md:aspect-auto md:h-[270px]",
              "col-span-6 md:col-span-5 aspect-[3/4] md:h-[270px]",
              "col-span-6 md:col-span-4 aspect-[3/4] md:h-[350px]",
              "col-span-12 md:col-span-8 aspect-[16/10] md:h-[350px]",
            ][i] || "col-span-6 md:col-span-4 aspect-[3/4]";

            return (
              <motion.button
                key={m.id}
                onClick={() => setOpen(m)}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.8, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                className={`group relative ${layout} rounded-[20px] overflow-hidden border border-white/10 bg-black`}
              >
                <img
                  src={resolveFileUrl(m.image)}
                  alt={m.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                />
                {/* dark gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                {/* grain */}
                <div className="absolute inset-0 opacity-[0.08] mix-blend-overlay pointer-events-none" style={{
                  backgroundImage: "radial-gradient(rgba(255,255,255,0.5) 1px, transparent 1px)",
                  backgroundSize: "3px 3px",
                }} />

                {/* Top-left tag */}
                <div className="absolute top-4 left-4 flex items-center gap-2">
                  <span
                    className="px-2.5 py-1 rounded-full text-[10px] tracking-[0.25em] uppercase font-mono backdrop-blur-md border"
                    style={{
                      borderColor: `${TAG_COLOR[m.tag] || "#fff"}55`,
                      color: TAG_COLOR[m.tag] || "#fff",
                      background: `${TAG_COLOR[m.tag] || "#fff"}10`,
                    }}
                  >
                    {m.tag}
                  </span>
                </div>

                {/* Bottom caption */}
                <div className="absolute bottom-0 inset-x-0 p-5 md:p-6 text-left">
                  <div
                    className="text-white text-[18px] md:text-[22px] leading-tight"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {m.title}
                  </div>
                  <div className="mt-1.5 text-[12px] text-white/70 line-clamp-2">{m.caption}</div>
                </div>

                <div className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 backdrop-blur-md border border-white/20 grid place-items-center text-white opacity-0 group-hover:opacity-100 transition-opacity">
                  <ArrowUpRight className="w-4 h-4" />
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(null)}
            className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-2xl flex items-center justify-center p-6"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-[1100px] w-full max-h-[88vh] rounded-[24px] overflow-hidden border border-white/10 bg-black"
            >
              <img src={resolveFileUrl(open.image)} alt={open.title} className="w-full h-auto max-h-[78vh] object-contain bg-black" />
              <div className="p-6 md:p-8 bg-gradient-to-t from-black to-black/80">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-[11px] tracking-[0.4em] uppercase text-[#e6c769] font-mono">{open.tag}</div>
                    <h3
                      className="mt-2 text-[26px] md:text-[36px] leading-tight text-white"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      {open.title}
                    </h3>
                    <div className="mt-2 text-white/70 text-[14px]">{open.caption}</div>
                  </div>
                  <button
                    onClick={() => setOpen(null)}
                    className="w-10 h-10 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 grid place-items-center text-white shrink-0"
                    aria-label="Close"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Moments;
